import { KeyboardEvent, MouseEvent, memo, useCallback, ReactNode } from 'react';
import { useDispatch } from 'react-redux';

import Button, { ButtonKind } from 'bloko/blocks/button';
import Column from 'bloko/blocks/column';
import { H2 } from 'bloko/blocks/header';
import Loading, { LoadingScale, LoadingColor } from 'bloko/blocks/loading';
import VSpacing from 'bloko/blocks/vSpacing';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import { addItem } from 'src/api/price/cart';
import FlexibleRow from 'src/components/FlexibleRow';
import { useNotification } from 'src/components/Notifications/Provider';
import translation from 'src/components/translation';
import { useSelectorNonNullable } from 'src/hooks/useSelector';
import { Tariff } from 'src/models/price/priceZp';

import { GbbControls, GbbDescription, GbbHeader, GbbItem, GbbPrice } from 'src/components/Price/Gbb';
import TariffDescription from 'src/components/Price/TariffDescription';

const TrlKeys = {
    toCart: 'employer.price2.addtocart',
};

const TariffCard: TranslatedComponent<{
    tariff: Tariff;
    features: ReactNode;
    controls: ReactNode;
}> = ({ features, controls, tariff, trls }) => {
    const currency = useSelectorNonNullable(({ priceData }) => priceData.currency);
    const product = tariff.product;
    const dispatch = useDispatch();
    const { addNotification } = useNotification();

    const addProductToCart = useCallback(
        (event: KeyboardEvent | MouseEvent) => {
            product && void dispatch(addItem(product, event.target, addNotification));
        },
        [dispatch, product, addNotification]
    );

    return (
        <Column xs="4" s="8" m="4" l="4">
            <GbbItem vertical flat={false} data-qa={`tariff_${tariff.tariffName}`}>
                <GbbDescription>
                    <GbbHeader>
                        <H2>{tariff.translations.title}</H2>
                        {!tariff.backoffice && <TariffDescription tariffName={tariff.tariffName} />}
                        <VSpacing base={5} />
                    </GbbHeader>
                    {features}
                </GbbDescription>
                <GbbControls>
                    {controls && (
                        <>
                            {controls}
                            <VSpacing base={6} />
                        </>
                    )}
                    <FlexibleRow>
                        <GbbPrice price={product?.price || 0} currency={currency} />
                        <div>
                            <Button
                                kind={ButtonKind.Primary}
                                onClick={addProductToCart}
                                loading={
                                    tariff.loading && (
                                        <Loading initial={LoadingColor.White} scale={LoadingScale.Small} />
                                    )
                                }
                                disabled={tariff.loading || !product?.price}
                                data-qa={product ? `add-to-cart-${product.tariff}` : ''}
                            >
                                {trls[TrlKeys.toCart]}
                            </Button>
                        </div>
                    </FlexibleRow>
                </GbbControls>
            </GbbItem>
        </Column>
    );
};

export default memo(translation(TariffCard));
